/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { type JSX, Suspense } from 'react';

const ImageComponent = React.lazy(
  // @ts-ignore
  () => import('../ImageComponent'),
);

export interface ImagePayload {
  altText: string;
  caption?: LexicalEditor;
  height?: number;
  key?: NodeKey;
  maxWidth?: number;
  showCaption?: boolean;
  src: string;
  width?: number;
  captionsEnabled?: boolean;
  isFileUpload?: boolean;
  href?: string;
  inline?: boolean;
  format?: ElementFormatType;
}

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src, width, height } = domNode;
    const inline = domNode.getAttribute?.('data-inline') === 'true';
    const format =
      (domNode.getAttribute?.('data-format') as ElementFormatType) || '';
    const node = $createImageNode({
      altText,
      height,
      src,
      width,
      inline,
      format,
    });
    return { node };
  }
  return null;
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    caption: SerializedEditor;
    height?: number;
    maxWidth?: number;
    showCaption: boolean;
    src: string;
    width?: number;
    isFileUpload?: boolean;
    inline?: boolean;
    format?: ElementFormatType;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: 'inherit' | number;
  __height: 'inherit' | number;
  __maxWidth?: number;
  __showCaption: boolean;
  __caption: LexicalEditor;
  // Captions cannot yet be used within editor cells
  __captionsEnabled: boolean;
  __isFileUpload: boolean;
  __inline: boolean;
  __format: ElementFormatType;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__isFileUpload,
      node.__inline,
      node.__format,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, height, width, maxWidth, caption, src, showCaption } =
      serializedNode;
    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width,
      inline: serializedNode.inline,
      format: serializedNode.format,
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    if (this.__inline) {
      element.setAttribute('data-inline', 'true');
    }
    if (this.__format) {
      element.setAttribute('data-format', this.__format);
      // 조회(ContentHtmlRender)엔 부모 text-align 이 없으므로 img 를 block+margin auto 로 직접 정렬.
      if (!this.__inline && this.__format !== 'left') {
        element.style.display = 'block';
        element.style.marginLeft = 'auto';
        element.style.marginRight = this.__format === 'center' ? 'auto' : '0';
      }
    }
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    maxWidth?: number,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    captionsEnabled?: boolean,
    isFileUpload?: boolean,
    inline?: boolean,
    format?: ElementFormatType,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
    this.__isFileUpload = isFileUpload || false;
    this.__inline = inline ?? false;
    this.__format = format ?? '';
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
      isFileUpload: this.__isFileUpload,
      inline: this.__inline,
      format: this.__format,
    };
  }

  setWidthAndHeight(
    width: 'inherit' | number,
    height: 'inherit' | number,
  ): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  setSrc(url: string): void {
    const writable = this.getWritable();
    writable.__src = url;
  }

  setFileUploadCheck(): void {
    const writable = this.getWritable();
    writable.__isFileUpload = true;
  }

  setFormat(format: ElementFormatType): this {
    const writable = this.getWritable();
    writable.__format = format;
    return writable;
  }

  setInline(inline: boolean): this {
    const writable = this.getWritable();
    writable.__inline = inline;
    return writable;
  }

  getFormat(): ElementFormatType {
    return this.__format;
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    // block 이미지 정렬: span(inline-block)을 block+fit-content 로 만들고 margin auto 로 가운데/우측.
    // FORMAT_ELEMENT 는 Element 만 정렬해 데코레이터엔 안 먹으므로 여기서 직접 반영(ImagesPlugin 이 setFormat 호출).
    if (
      !this.__inline &&
      (this.__format === 'center' || this.__format === 'right')
    ) {
      span.style.display = 'block';
      span.style.width = 'fit-content';
      span.style.marginLeft = 'auto';
      span.style.marginRight = this.__format === 'center' ? 'auto' : '0';
    }
    return span;
  }

  updateDOM(prevNode: ImageNode): boolean {
    // 정렬·inline 이 바뀌면 DOM 을 재생성(createDOM)해 반영. 그 외에는 기존처럼 갱신 불필요.
    return (
      prevNode.__format !== this.__format || prevNode.__inline !== this.__inline
    );
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  getIsFileUpload(): boolean {
    return this.__isFileUpload;
  }

  getInline(): boolean {
    return this.__inline;
  }

  isInline(): boolean {
    return this.__inline;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          maxWidth={this.__maxWidth}
          nodeKey={this.getKey()}
          showCaption={this.__showCaption}
          caption={this.__caption}
          captionsEnabled={this.__captionsEnabled}
          resizable={true}
        />
      </Suspense>
    );
  }
}

export function $createImageNode({
  altText,
  height,
  maxWidth = undefined,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  isFileUpload,
  inline,
  format,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(
      src,
      altText,
      maxWidth,
      width,
      height,
      showCaption,
      caption,
      captionsEnabled,
      isFileUpload,
      inline,
      format,
      key,
    ),
  );
}

export function $isImageNode(
  node: LexicalNode | null | undefined,
): node is ImageNode {
  return node instanceof ImageNode;
}
